import React from "react";
import { useSelector } from "react-redux";

var slugify = require("slugify");

export default function useAddDimension(props) {
  const [open, setOpen] = React.useState(false);
  const [dimension, setDimension] = React.useState("");
  const [optionalDimension, setOptionalDimension] = React.useState(true);
  const [multipleChoice, setMultipleChoice] = React.useState(false);
  const [maxChoices, setMaxChoices] = React.useState(1);
  const [type, setType] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [openSnack, setOpenSnack] = React.useState(true);
  const [snackbarMessage, setSnackMessage] = React.useState("");
  const [paidSupplement, setPaidSupplement] = React.useState(false);
  const [amountSupplement, setAmountSupplement] = React.useState(0);
  const [editingDimension, setEditingDimension] = React.useState(null);

  // États pour la fonctionnalité de copie
  const [copyMode, setCopyMode] = React.useState(false);
  const [selectedItemId, setSelectedItemId] = React.useState("");
  const [selectedDimensionType, setSelectedDimensionType] = React.useState("");
  const [copiedItems, setCopiedItems] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");

  const { onAddDimension, onUpdateDimension } = props;

  // Récupérer les items depuis Redux
  const items = useSelector((state) => state.items?.items || []);

  const openDialog = () => {
    setOpen(true);
  };

  const openDialogWithDimension = (dimensionData) => {
    setEditingDimension(dimensionData);
    setDimension(dimensionData.title || "");
    setType(dimensionData.type || "");
    setOptionalDimension(dimensionData.optionalDimension !== false);
    setMultipleChoice(dimensionData.multipleChoice || false);
    setMaxChoices(dimensionData.maxChoices || 1);
    setPaidSupplement(dimensionData.paidSupplement || false);
    setAmountSupplement(dimensionData.amountSupplement || 0);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    // Réinitialiser les champs après fermeture
    setTimeout(() => {
      setEditingDimension(null);
      setDimension("");
      setType("");
      setOptionalDimension(true);
      setMultipleChoice(false);
      setMaxChoices(1);
      setPaidSupplement(false);
      setAmountSupplement(0);
      setCopyMode(false);
      setSelectedItemId("");
      setSelectedDimensionType("");
      setCopiedItems([]);
      setSearchTerm("");
    }, 300);
  };

  const handleCancel = () => {
    closeDialog();
  };

  const handleCloseSnack = () => {
    setOpenSnack(false);
    setSubmitting(false);
  };

  const handleOptionalDimensionChange = (e) => {
    const { checked } = e.target;
    // Si "Obligatoire" est coché, optionalDimension doit être false
    setOptionalDimension(!checked);
  };

  const handleDimensionChange = (e) => {
    const { value } = e.target;
    if (value.length <= 20) {
      setDimension(e.target.value);
    }
  };

  const handleMultipleChoiceChange = (e) => {
    const { checked } = e.target;
    setMultipleChoice(checked);
  };

  const handleMaxChoicesChange = (e) => {
    const { value } = e.target;
    setMaxChoices(value);
  };

  const handlePaidSupplementChange = (e) => {
    const { checked } = e.target;
    setPaidSupplement(checked);
  };

  const handleAmountSupplementChange = (e) => {
    const { value } = e.target;
    setAmountSupplement(value);
  };

  const handleTypeChange = (e) => {
    const type = slugify(e.target.value);
    if (type.length <= 10) {
      setType(type.toUpperCase());
    }
  };

  const handleCopyModeChange = (event) => {
    setCopyMode(event.target.checked);
    if (!event.target.checked) {
      // Réinitialiser les sélections si on désactive le mode copie
      setSelectedItemId("");
      setSelectedDimensionType("");
      setCopiedItems([]);
      setSearchTerm("");
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleItemSelect = (event) => {
    const itemId = event.target.value;
    setSelectedItemId(itemId);
    setSelectedDimensionType(""); // Réinitialiser la dimension sélectionnée
  };

  const handleDimensionSelect = (event) => {
    const dimensionType = event.target.value;
    setSelectedDimensionType(dimensionType);

    // Trouver l'item sélectionné
    const selectedItem = items.find((item) => item.id === selectedItemId);
    if (!selectedItem) return;

    // Trouver la dimension sélectionnée
    const dimensionToCopy = selectedItem.dimensions?.find(
      (d) => d.type === dimensionType
    );
    if (!dimensionToCopy) return;

    // Copier tous les champs de la dimension
    setDimension(dimensionToCopy.title || "");
    setType(dimensionToCopy.type || "");
    setOptionalDimension(dimensionToCopy.optionalDimension !== false);
    setMultipleChoice(dimensionToCopy.multipleChoice || false);
    setMaxChoices(dimensionToCopy.maxChoices || 1);
    setPaidSupplement(dimensionToCopy.paidSupplement || false);
    setAmountSupplement(dimensionToCopy.amountSupplement || 0);

    // Copier les items de la dimension (deep copy pour éviter les références)
    setCopiedItems(
      dimensionToCopy.items
        ? JSON.parse(JSON.stringify(dimensionToCopy.items))
        : []
    );
  };

  // Obtenir les dimensions disponibles pour l'item sélectionné
  const availableDimensions = React.useMemo(() => {
    if (!selectedItemId) return [];
    const selectedItem = items.find((item) => item.id === selectedItemId);
    return selectedItem?.dimensions || [];
  }, [selectedItemId, items]);

  // Filtrer et trier les items par ordre alphabétique
  const filteredAndSortedItems = React.useMemo(() => {
    let filtered = items;
    
    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = items.filter((item) =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Trier par ordre alphabétique
    return filtered.sort((a, b) => {
      const titleA = a.title?.toLowerCase() || "";
      const titleB = b.title?.toLowerCase() || "";
      return titleA.localeCompare(titleB);
    });
  }, [items, searchTerm]);

  const handleSave = () => {
    setSubmitting(true);
    setOpenSnack(false);

    let response;
    if (editingDimension && onUpdateDimension) {
      // Mode édition : mettre à jour la dimension existante
      response = onUpdateDimension(
        editingDimension.type, // originalType
        dimension,
        type,
        multipleChoice,
        maxChoices,
        paidSupplement,
        amountSupplement,
        optionalDimension
      );
    } else {
      // Mode ajout : créer une nouvelle dimension
      response = onAddDimension(
        dimension,
        type,
        multipleChoice,
        maxChoices,
        paidSupplement,
        amountSupplement,
        optionalDimension,
        copiedItems // Passer les items copiés
      );
    }

    if (!!response) {
      setDimension("");
      setType("");
      setMultipleChoice(false);
      setSubmitting(false);
      closeDialog();
    } else {
      setSnackMessage(
        "Sauvegarde non possible. Vérifiez que la caractéristique n'existe pas déjà"
      );
      setOpenSnack(true);
    }
  };

  return {
    open,
    optionalDimension,
    dimension,
    multipleChoice,
    maxChoices,
    paidSupplement,
    amountSupplement,
    type,
    submitting,
    openSnack,
    snackbarMessage,
    editingDimension,
    copyMode,
    selectedItemId,
    selectedDimensionType,
    items,
    filteredAndSortedItems,
    availableDimensions,
    searchTerm,
    openDialog,
    openDialogWithDimension,
    closeDialog,
    onSave: handleSave,
    onCancel: handleCancel,
    onOptionalDimensionChange: handleOptionalDimensionChange,
    onDimensionChange: handleDimensionChange,
    onMultipleChoiceChange: handleMultipleChoiceChange,
    onMaxChoicesChange: handleMaxChoicesChange,
    onPaidSupplementChange: handlePaidSupplementChange,
    onAmountSupplementChange: handleAmountSupplementChange,
    onTypeChange: handleTypeChange,
    onCloseSnack: handleCloseSnack,
    onCopyModeChange: handleCopyModeChange,
    onItemSelect: handleItemSelect,
    onDimensionSelect: handleDimensionSelect,
    onSearchChange: handleSearchChange,
  };
}
